// Welder WQTR Register — CA-11.
// Records each welder's WQTR (Welder Qualification Test Record) with the joint-type
// coverage the weld-assignment gate reads. AISC 207-25 §2.5.6 + AWS D1.1 §4.
// Backed by /api/wpqr-documents (tenant-scoped, audit-logged).

import React, { useEffect, useState, useCallback } from "react";
import PageGuide from "../../components/PageGuide";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, Stack, Typography, Breadcrumbs, Button, Paper, Box, Alert,
  TableHead, TableRow, TableCell, TableBody, TableContainer,
  Chip, FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import ApiCall from "../../utils/apicall";

import SmartTable from "../../components/SmartTable/SmartTable";
const CODES = ["D1.1", "D1.2", "D1.3", "D1.4", "D1.5", "D1.6", "D1.8", "API1104", "OTHER"];
const PROCESSES = ["SMAW", "GMAW", "FCAW", "MCAW", "GTAW", "SAW"];
const STATUS_COLORS = { active: "success", suspended: "warning", expired: "error", revoked: "default" };

const EMPTY = {
  welder_id: "", wqtr_number: "", welding_code: "D1.1", process: "SMAW",
  joint_types: "", qualified_at: "", expires_at: "", status: "active", notes: "",
};

function isExpired(d) { return d && new Date(d) < new Date(); }

export default function WelderWqtr() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ApiCall.get("/wpqr-documents", { params: statusFilter ? { status: statusFilter } : {} });
      setRows(res?.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to load WQTRs");
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    const joint_types = String(form.joint_types).split(",").map((s) => s.trim()).filter(Boolean);
    if (!form.welder_id) { toast.error("Welder ID is required"); return; }
    if (joint_types.length === 0) { toast.error("At least one joint type is required (comma-separated)"); return; }
    setSaving(true);
    try {
      await ApiCall.post("/wpqr-documents", { ...form, welder_id: Number(form.welder_id), joint_types });
      toast.success("WQTR recorded.");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Container maxWidth="xl">
      <Helmet><title>Welder WQTR Register | SFM</title></Helmet>
      <Breadcrumbs sx={{ mb: 1 }}>
        <RouterLink to="/dashboard/app" style={{ color: "inherit" }}>Home</RouterLink>
        <Typography color="text.primary">Welder WQTR Register</Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4">Welder WQTR Register</Typography>
          <Typography variant="body2" color="text.secondary">
            AISC 207-25 §2.5.6 + AWS D1.1 §4 — each welder&apos;s qualification test record and the joint types it covers.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => { setForm(EMPTY); setOpen(true); }}>+ Add WQTR</Button>
      </Stack>

      <PageGuide
        why="Prove each welder is qualified for the joints they weld — the record the weld-assignment gate checks."
        who="The QM, maintaining welder qualifications for AISC certification."
        what="Each welder's WQTR: code, process, the joint types it covers, and its expiry."
        how="Add a WQTR per welder; list the joint types (e.g. CJP-PLATE, PJP-PIPE) it qualifies them for."
        when="When a welder qualifies or requalifies, and as WQTRs approach expiry."
        standard="AISC 207-25 §2.5.6 · AWS D1.1 §4"
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Once each active welder has a current WQTR here covering their joint types, the weld-assignment
        gate can be switched to enforce (see the CA-11 enablement runbook). Until then it stays advisory.
      </Alert>

      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="suspended">Suspended</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="revoked">Revoked</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <SmartTable tableId="welder-wqtr" lockedColumns={["Welder ID", "WQTR #", "Expires", "Status"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Welder ID</TableCell>
                <TableCell>WQTR #</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Process</TableCell>
                <TableCell>Joint Types</TableCell>
                <TableCell>Qualified</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  No WQTRs recorded yet. Add one per welder to build the qualification register.
                </TableCell></TableRow>
              ) : rows.map((r) => {
                const exp = isExpired(r.expires_at);
                const jt = Array.isArray(r.joint_types) ? r.joint_types : [];
                return (
                  <TableRow key={r.id} hover>
                    <TableCell><strong>{r.welder_id}</strong></TableCell>
                    <TableCell>{r.wqtr_number || "—"}</TableCell>
                    <TableCell>{r.welding_code || "—"}</TableCell>
                    <TableCell>{r.process || "—"}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {jt.map((t) => <Chip key={t} label={t} size="small" variant="outlined" />)}
                      </Stack>
                    </TableCell>
                    <TableCell>{r.qualified_at || "—"}</TableCell>
                    <TableCell sx={{ color: exp ? "error.dark" : undefined, fontWeight: exp ? 600 : 400 }}>
                      {r.expires_at || "—"} {exp && "⚠ expired"}
                    </TableCell>
                    <TableCell><Chip label={r.status} color={STATUS_COLORS[r.status] || "default"} size="small" /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {rows.length} WQTR{rows.length === 1 ? "" : "s"} on file.
      </Typography>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add WQTR</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}><TextField fullWidth size="small" label="Welder ID" name="welder_id" value={form.welder_id} onChange={handleChange} type="number" required /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="WQTR Number" name="wqtr_number" value={form.wqtr_number} onChange={handleChange} /></Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small"><InputLabel>Welding Code</InputLabel>
                <Select label="Welding Code" name="welding_code" value={form.welding_code} onChange={handleChange}>
                  {CODES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small"><InputLabel>Process</InputLabel>
                <Select label="Process" name="process" value={form.process} onChange={handleChange}>
                  {PROCESSES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Joint Types (comma-separated)" name="joint_types"
                value={form.joint_types} onChange={handleChange} placeholder="CJP-PLATE, PJP-PIPE" required
                helperText="The joint-type codes this WQTR qualifies the welder for — the gate matches assignments against these." />
            </Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Qualified Date" name="qualified_at" value={form.qualified_at} onChange={handleChange} type="date" InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Expires" name="expires_at" value={form.expires_at} onChange={handleChange} type="date" InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small"><InputLabel>Status</InputLabel>
                <Select label="Status" name="status" value={form.status} onChange={handleChange}>
                  {["active", "suspended", "expired", "revoked"].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Notes" name="notes" value={form.notes} onChange={handleChange} multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save WQTR"}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
